import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { fmtSize, uploadLeadDocument } from "@/lib/files";
import { Label } from "@/components/ui/label";
import { FileText, Image as ImageIcon, Loader2, Paperclip, X } from "lucide-react";

const ACCEPT = "application/pdf,image/*,.heic,.heif";

export function DocIcon({ kind, className = "h-4 w-4" }) {
  return kind === "pdf" ? <FileText className={`${className} text-rose-600`} /> : <ImageIcon className={`${className} text-sky-600`} />;
}

/* Pick + upload property papers (PDF / photos). Calls onAdd(doc) per uploaded file and onRemove(file_id). */
export default function LeadDocumentsField({ docs, onAdd, onRemove, label = "Property documents (optional)", hint, testId = "lead-docs" }) {
  const [state, setState] = useState(null); // { index, total, pct }
  const inputRef = useRef(null);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    let ok = 0;
    for (let i = 0; i < files.length; i += 1) {
      setState({ index: i + 1, total: files.length, pct: 0 });
      try {
        const doc = await uploadLeadDocument(files[i], (pct) => setState((s) => s && { ...s, pct }));
        await onAdd(doc);
        ok += 1;
      } catch (err) {
        toast.error(`${files[i].name}: ${err?.response ? formatApiError(err) : err.message}`, { duration: 9000 });
      }
    }
    setState(null);
    if (ok) toast.success(`${ok} document(s) attached`);
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <Label>{label}</Label>
        <span className="text-[11px] text-slate-500" data-testid={`${testId}-count`}>{docs.length} attached</span>
      </div>
      {hint && <p className="text-xs text-slate-500 mb-2">{hint}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={!!state}
          className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm bg-white hover:bg-slate-50 disabled:opacity-60"
          data-testid={`${testId}-pick`}>
          {state ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          {state ? `Uploading ${state.index}/${state.total} · ${state.pct}%` : "Attach sale deed, sanction letter, ID…"}
        </button>
        <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={pick} data-testid={`${testId}-input`} />
        <span className="text-[11px] text-slate-400">PDF · JPG · PNG · up to 15 MB each (photos are compressed automatically)</span>
      </div>
      {docs.length > 0 && (
        <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200 bg-white" data-testid={`${testId}-list`}>
          {docs.map((d) => (
            <li key={d.file_id} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`${testId}-item-${d.file_id}`}>
              <DocIcon kind={d.kind} />
              <span className="truncate flex-1">{d.name}</span>
              <span className="text-[11px] text-slate-400 num">{fmtSize(d.size)}</span>
              {onRemove && (
                <button type="button" onClick={() => onRemove(d.file_id)} className="p-1 rounded hover:bg-red-50" aria-label={`Remove ${d.name}`} data-testid={`${testId}-remove-${d.file_id}`}>
                  <X className="h-3.5 w-3.5 text-red-600" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
